import React, {useContext, useEffect, useLayoutEffect, useRef, useState} from 'react';
import 'styles/chat/Chat.css';
import 'styles/chat/ChatScrollbar.css';
import {getLastMessagesRequest, getUnitPriceRequest} from 'util/api';
import MessageItem from './MessageItem';
import {WebSocketContext} from 'wss/WebSocketService';
import {useNotification} from 'util/NotificationContext';
import SimpleArrowDownSvg from 'util/svg/SimpleArrowDownSvg';
import ChatInput from 'components/chat/ChatInput';
import {useSessionConfiguration} from 'util/SessionConfigurationContext';

const wssChannelName = 'chat';

const Chat = ({isMain = false}) => {
    const [messages, setMessages] = useState([]);
    const [unitPrice, setUnitPrice] = useState(1);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const wsService = useContext(WebSocketContext);
    const [showScrollToBottomArrow, setShowScrollToBottomArrow] = useState(false);
    const isScrolledToBottomRef = useRef(true);
    const shouldScrollToBottomRef = useRef(false);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const {resetMissedMessages} = useNotification();
    const {isTouch, isDesktopView} = useSessionConfiguration();

    const scrollInstantlyToBottom = () => {
        messagesEndRef.current?.scrollIntoView({behavior: 'instant'});
    };

    useEffect(() => {
        const setChatContainerHeight = () => {
            const chatWrapper = document.querySelector('.chat-wrapper');
            const mainChatContainer = document.querySelector('.main-chat-container');
            if (chatWrapper && mainChatContainer) {
                console.log('chatWrapper.clientHeight', chatWrapper.clientHeight);
                mainChatContainer.style.height = `${chatWrapper.clientHeight}px`;
            }
        };

        const uglySetChatContainerHeight = () => {
            const windowHeight = window.innerHeight;
            const mainChatContainer = document.querySelector('.main-chat-container');
            if (mainChatContainer) {
                console.log('windowHeight', windowHeight);
                mainChatContainer.style.height = `${windowHeight - 120}px`;
                mainChatContainer.style.maxHeight = `${windowHeight - 120}px`;
            }
        };

        if (isMain && isTouch) {
            window.addEventListener('load', uglySetChatContainerHeight);
            window.addEventListener('resize', uglySetChatContainerHeight);
        } else {
            window.addEventListener('load', setChatContainerHeight);
            window.addEventListener('resize', setChatContainerHeight);

            setChatContainerHeight();
        }

        return () => {
            window.removeEventListener('load', uglySetChatContainerHeight);
            window.removeEventListener('resize', uglySetChatContainerHeight);
            window.removeEventListener('load', setChatContainerHeight);
            window.removeEventListener('resize', setChatContainerHeight);
        };
    }, [isMain]);

    useEffect(() => {
        resetMissedMessages();

        const handleMessage = (content, topic) => {
            switch (topic) {
                case 'textMessage':
                    if (isScrolledToBottomRef.current) {
                        shouldScrollToBottomRef.current = true;
                    }
                    setMessages((prevMessages) => [...prevMessages, content]);
                    break;
                case 'newMessagePrice':
                    setUnitPrice(content.newPrice);
                    break;
                default:
                    throw new Error(`Unknown topic: ${topic}`);
            }
        };

        const fetchLastMessages = async () => {
            setIsLoadingMessages(true);
            try {
                const data = await getLastMessagesRequest();
                setMessages(data);
                scrollInstantlyToBottom();
            } catch (error) {
                console.error('Error fetching last messages:', error);
            } finally {
                setIsLoadingMessages(false);
            }
        };
        const fetchUnitPrice = async () => {
            try {
                const priceData = await getUnitPriceRequest();
                setUnitPrice(priceData.unitPrice);
            } catch (error) {
                console.error('Error fetching unit price:', error);
            }
        };

        fetchUnitPrice();
        fetchLastMessages();

        wsService.bindChannel(wssChannelName);
        wsService.onMessage(wssChannelName, handleMessage);

        return () => {
            wsService.offMessage(wssChannelName, handleMessage);
            wsService.unbindChannel(wssChannelName);
        };
    }, []);


    useEffect(() => {
        const fetchLastMessages = async () => {
            try {
                const data = await getLastMessagesRequest();
                setMessages(data);
                scrollInstantlyToBottom();
            } catch (error) {
                console.error('Error fetching last messages:', error);
            }
        };
        fetchLastMessages();
    }, []);

    useLayoutEffect(() => {
        if (shouldScrollToBottomRef.current) {
            scrollInstantlyToBottom();
            shouldScrollToBottomRef.current = false; // Reset the ref
        }
    }, [messages]);

    const handleScroll = () => {
        const messagesContainer = messagesContainerRef.current;
        if (messagesContainer) {
            const {scrollTop, scrollHeight, clientHeight} = messagesContainer;
            const isAtBottom = scrollHeight - scrollTop <= clientHeight + 1; // Small tolerance
            isScrolledToBottomRef.current = isAtBottom;
            setShowScrollToBottomArrow(!isAtBottom);
        }
    };

    const smoothScrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
    };

    return (
        <div
            className={`chat-wrapper 
                 ${isTouch ? 'mobile' : ''} 
                ${isMain && !isDesktopView ? 'main-mobile-chat' : ''}`
            }>
            <div className="main-chat-container">
                <div
                    className="messages-container"
                    onScroll={handleScroll}
                    ref={messagesContainerRef}
                >
                    {isLoadingMessages && <div className="loading-label">Loading...</div>}
                    {messages.length === 0 && !isLoadingMessages && (
                        <div className="no-messages-label">There are no messages in this room yet</div>
                    )}
                    {messages.map((msg, index) => (
                        <MessageItem
                            key={index}
                            msg={msg}
                            isBottomMessage={index === messages.length - 1 && messages.length > 1}
                        />
                    ))}
                    <div ref={messagesEndRef}></div>
                </div>

                <div style={{position: 'relative'}}>
                    {showScrollToBottomArrow && (
                        <div className="scroll-to-bottom-arrow" onClick={smoothScrollToBottom}>
                            <SimpleArrowDownSvg/>
                        </div>
                    )}
                    <ChatInput
                        unitPrice={unitPrice}
                        onSendMessage={() => {
                            scrollInstantlyToBottom();
                        }}
                        onHeightResize={() => {
                            if (isScrolledToBottomRef.current) {
                                scrollInstantlyToBottom();
                            }
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default Chat;
